import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bike, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Eye, EyeOff, ImagePlus, Link2, LogOut, Pencil, Plus, RefreshCw, Star, Trash2, Undo2, UtensilsCrossed, Check, X } from "lucide-react";
import { ORDER_CATEGORIES, ORDER_ITEMS } from "../data/order";
import { Blossom, Flourish, PetalField } from "../components/decor";

const API = "/api";

const STATUS_STYLES = {
  pending: "bg-[#C9A227]/15 text-[#8a6d12] border-[#C9A227]/50",
  preparing: "bg-[#9B2C2C]/10 text-[#9B2C2C] border-[#9B2C2C]/40",
  completed: "bg-green-700/10 text-green-800 border-green-700/40",
};

const StatusBadge = ({ status }) => (
  <span data-testid="order-status-badge" className={`inline-block rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
    {status}
  </span>
);

const AdminLogin = ({ onUnlock }) => {
  const [form, setForm] = useState({ email: "", password: "", pin: "" });
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), password: form.password, pin: form.pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Login failed — check credentials");
        return;
      }
      sessionStorage.setItem("bloomora-admin-token", data.token);
      onUnlock(data.token);
    } catch {
      setError("Could not reach the kitchen server. Try again.");
    } finally {
      setChecking(false);
    }
  };

  const field = (key, label, type, testid, props = {}) => (
    <div className="mt-4 text-left">
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-3 text-sm text-[#2C2C2C] outline-none transition-colors focus:border-[#9B2C2C]"
        {...props}
      />
    </div>
  );

  return (
    <main data-testid="admin-pin-gate" className="relative flex min-h-[80vh] items-center justify-center overflow-hidden px-5">
      <PetalField count={9} />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-sm rounded-[16px] border border-[#E8B4B8]/40 bg-white/85 p-8 text-center shadow-[0_24px_60px_rgba(155,44,44,0.14)] backdrop-blur-md"
      >
        <img src="/images/logo.webp" alt="Bloomora" className="mx-auto h-16 w-16 rounded-full object-cover shadow-md" />
        <h1 className="mt-3 font-serif text-3xl font-bold text-[#9B2C2C]">Staff Access</h1>
        <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[#B86B77]">Bloomora Admin Panel</p>
        <div className="mt-4 flex justify-center"><Flourish className="h-3.5 w-40" /></div>
        {field("email", "Admin Email", "email", "admin-email-input", { placeholder: "you@gmail.com", autoComplete: "username" })}
        {field("password", "Password", "password", "admin-password-input", { placeholder: "••••••••••", autoComplete: "current-password" })}
        {field("pin", "Admin PIN", "password", "admin-pin-input", { placeholder: "4-digit PIN", inputMode: "numeric", maxLength: 4 })}
        {error && <p data-testid="admin-pin-error" className="mt-3 text-xs font-semibold text-[#9B2C2C]">{error}</p>}
        <button
          data-testid="admin-pin-submit"
          disabled={!form.email || !form.password || form.pin.length !== 4 || checking}
          className="mt-6 w-full rounded-full bg-[#9B2C2C] py-3.5 font-semibold text-white transition-colors hover:bg-[#7A1D28] disabled:opacity-50"
        >
          {checking ? "Verifying..." : "Enter Dashboard"}
        </button>
        <p className="mt-4 text-[11px] text-[#B86B77]">Authorised staff only. All attempts are logged.</p>
      </motion.form>
    </main>
  );
};

const AddDishModal = ({ categories, defaultCat, onClose, onSaved, headers, onUnauthorized }) => {
  const [form, setForm] = useState({ name: "", price: "", desc: "", cat: defaultCat === "all" ? "dimsum" : defaultCat, veg: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const res = await fetch(`${API}/admin/menu-item`, {
      method: "POST",
      headers,
      body: JSON.stringify({ ...form, price: Number(form.price) }),
    }).catch(() => null);
    setSaving(false);
    if (!res) { setError("Network error — try again"); return; }
    if (res.status === 401) { onUnauthorized(); return; }
    if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.detail || "Could not save dish"); return; }
    onSaved();
    onClose();
  };

  return (
    <div data-testid="add-dish-modal" className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2C2C2C]/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-md rounded-[16px] border border-[#E8B4B8]/40 bg-[#FFF7F8] p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl font-bold text-[#9B2C2C]">Add New Dish</h3>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-[#9B2C2C] hover:bg-[#E8B4B8]/30" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="mt-4 flex flex-col gap-3.5">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">Dish Name</label>
            <input data-testid="new-dish-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Virgin Mint Mojito" className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#9B2C2C]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">Price (₹)</label>
              <input data-testid="new-dish-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value.replace(/[^0-9]/g, "") })} placeholder="199" inputMode="numeric" className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#9B2C2C]" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">Category</label>
              <select data-testid="new-dish-category" value={form.cat} onChange={(e) => setForm({ ...form, cat: e.target.value })} className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#9B2C2C]">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]">Description</label>
            <textarea data-testid="new-dish-desc" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} rows={2} placeholder="One short, tempting line…" className="w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#9B2C2C]" />
          </div>
          <button
            type="button"
            data-testid="new-dish-veg-toggle"
            onClick={() => setForm({ ...form, veg: !form.veg })}
            className="flex items-center justify-between rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-2.5"
          >
            <span className="text-sm font-semibold text-[#2C2C2C]">{form.veg ? "Vegetarian" : "Non-Vegetarian"}</span>
            <span className={`relative h-6 w-11 rounded-full transition-colors ${form.veg ? "bg-green-700" : "bg-[#8a4b2b]"}`}>
              <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-[left] ${form.veg ? "left-6" : "left-1"}`} />
            </span>
          </button>
          {error && <p data-testid="new-dish-error" className="text-xs font-semibold text-[#9B2C2C]">{error}</p>}
          <button
            data-testid="new-dish-save"
            disabled={saving || form.name.trim().length < 2 || !form.price}
            className="w-full rounded-full bg-[#9B2C2C] py-3.5 font-semibold text-white transition-colors hover:bg-[#7A1D28] disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Dish — Goes Live Instantly"}
          </button>
          <p className="text-center text-[11px] text-[#B86B77]">After saving, use the photo button on the dish row to add its picture.</p>
        </div>
      </motion.form>
    </div>
  );
};

const MiniToggle = ({ on, onClick, testid, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`w-11 text-right text-[10px] font-bold ${on ? "text-green-700" : "text-[#9B2C2C]"}`}>{label}</span>
    <button
      data-testid={testid}
      onClick={onClick}
      aria-label={label}
      className={`relative h-6 w-10 rounded-full transition-colors duration-300 ${on ? "bg-green-700" : "bg-[#E8B4B8]"}`}
    >
      <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-[left] duration-300 ${on ? "left-5" : "left-1"}`} />
    </button>
  </span>
);

const EditDishModal = ({ item, onClose, onSave }) => {
  const blankSizes = [
    { label: "Small 8in", price: "" },
    { label: "Medium 10in", price: "" },
    { label: "Large 12in", price: "" },
  ];
  const [form, setForm] = useState({
    name: item.name,
    price: String(item.price),
    desc: item.desc || "",
    veg: item.veg !== false,
    best: item.best === true,
    sizes: item.sizes?.length ? item.sizes.map((s) => ({ label: s.label, price: String(s.price) })) : blankSizes,
    addonPrice: String(item.addons?.[0]?.price ?? ""),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setSize = (i, key, val) =>
    setForm({ ...form, sizes: form.sizes.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const sizes = form.sizes.filter((s) => s.label.trim() && s.price).map((s) => ({ label: s.label.trim(), price: Number(s.price) }));
    const err = await onSave(item.id, {
      name: form.name.trim(),
      price: Number(form.price),
      desc: form.desc.trim(),
      veg: form.veg,
      best: form.best,
      sizes,
      addons: form.addonPrice ? [{ label: "Extra Cheese", price: Number(form.addonPrice) }] : [],
    });
    setSaving(false);
    if (err) { setError(err); return; }
    onClose();
  };

  const inp = "w-full rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#9B2C2C]";
  const lbl = "mb-1 block text-xs font-semibold uppercase tracking-wider text-[#9B2C2C]";

  return (
    <div data-testid="edit-dish-modal" className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2C2C2C]/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[16px] border border-[#E8B4B8]/40 bg-[#FFF7F8] p-6 shadow-2xl"
        data-lenis-prevent
      >
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl font-bold text-[#9B2C2C]">Edit Dish</h3>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-[#9B2C2C] hover:bg-[#E8B4B8]/30" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="mt-4 flex flex-col gap-3.5">
          <div>
            <label className={lbl}>Dish Name</label>
            <input data-testid="edit-dish-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Base Price (₹)</label>
            <input data-testid="edit-dish-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value.replace(/[^0-9]/g, "") })} inputMode="numeric" className={inp} />
          </div>
          <div>
            <label className={lbl}>Full Description</label>
            <textarea data-testid="edit-dish-desc" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} rows={3} className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" data-testid="edit-dish-veg" onClick={() => setForm({ ...form, veg: !form.veg })} className="flex items-center justify-between rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-2.5">
              <span className="text-xs font-semibold text-[#2C2C2C]">{form.veg ? "Veg" : "Non-Veg"}</span>
              <span className={`relative h-5 w-9 rounded-full transition-colors ${form.veg ? "bg-green-700" : "bg-[#8a4b2b]"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left] ${form.veg ? "left-4" : "left-0.5"}`} />
              </span>
            </button>
            <button type="button" data-testid="edit-dish-best" onClick={() => setForm({ ...form, best: !form.best })} className="flex items-center justify-between rounded-[10px] border border-[#E8B4B8]/60 bg-white px-4 py-2.5">
              <span className="text-xs font-semibold text-[#2C2C2C]">Bestseller</span>
              <span className={`relative h-5 w-9 rounded-full transition-colors ${form.best ? "bg-[#C9A227]" : "bg-[#E8B4B8]"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left] ${form.best ? "left-4" : "left-0.5"}`} />
              </span>
            </button>
          </div>
          <div>
            <label className={lbl}>Size Variants (leave prices blank for single-size items)</label>
            {form.sizes.map((s, i) => (
              <div key={i} className="mt-2 flex gap-2">
                <input data-testid={`edit-size-label-${i}`} value={s.label} onChange={(e) => setSize(i, "label", e.target.value)} placeholder="Size label" className={`${inp} flex-1`} />
                <input data-testid={`edit-size-price-${i}`} value={s.price} onChange={(e) => setSize(i, "price", e.target.value.replace(/[^0-9]/g, ""))} placeholder="₹" inputMode="numeric" className={`${inp} w-20`} />
              </div>
            ))}
          </div>
          <div>
            <label className={lbl}>Extra Cheese Add-on (₹, blank = none)</label>
            <input data-testid="edit-addon-price" value={form.addonPrice} onChange={(e) => setForm({ ...form, addonPrice: e.target.value.replace(/[^0-9]/g, "") })} inputMode="numeric" placeholder="e.g. 40" className={inp} />
          </div>
          {error && <p data-testid="edit-dish-error" className="text-xs font-semibold text-[#9B2C2C]">{error}</p>}
          <button data-testid="edit-dish-save" disabled={saving || form.name.trim().length < 2 || !form.price} className="w-full rounded-full bg-[#9B2C2C] py-3.5 font-semibold text-white transition-colors hover:bg-[#7A1D28] disabled:opacity-50">
            {saving ? "Saving..." : "Save Changes — Live Instantly"}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

const PriceEditor = ({ item, onSave }) => {
  const [val, setVal] = useState(String(item.price));
  useEffect(() => setVal(String(item.price)), [item.price]);
  const dirty = val !== String(item.price) && Number(val) > 0;
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-[#B86B77]">₹</span>
      <input
        data-testid={`price-input-${item.id}`}
        value={val}
        onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, ""))}
        inputMode="numeric"
        aria-label={`Price for ${item.name}`}
        className="w-16 rounded-md border border-[#E8B4B8]/60 bg-white px-2 py-1 text-sm font-semibold text-[#9B2C2C] outline-none focus:border-[#9B2C2C]"
      />
      {dirty && (
        <button
          data-testid={`price-save-${item.id}`}
          onClick={() => onSave(item.id, Number(val))}
          className="rounded-full bg-green-700 p-1.5 text-white transition-transform hover:scale-110"
          aria-label="Save price"
        >
          <Check size={13} />
        </button>
      )}
    </div>
  );
};

const OrderCard = ({ order, onStatus }) => {
  const time = new Date(order.created_at).toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
  return (
    <motion.article
      data-testid={`admin-order-${order.order_id}`}
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-[14px] border border-[#E8B4B8]/40 bg-white/85 p-5 shadow-[0_8px_26px_rgba(155,44,44,0.08)] backdrop-blur-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif text-lg font-bold text-[#9B2C2C]">{order.order_id}</p>
          <p className="text-xs text-[#B86B77]">{time}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#2C2C2C]">
        <span className="font-semibold">{order.name}</span>
        <a href={`tel:+91${order.phone}`} className="text-[#9B2C2C] underline-offset-2 hover:underline">{order.phone}</a>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F8E8E8] px-3 py-1 text-xs font-semibold text-[#9B2C2C]">
          {order.type === "dinein" ? <><UtensilsCrossed size={12} /> Dine-in • Table {order.table_no}</> : <><Bike size={12} /> Delivery</>}
        </span>
      </div>
      {order.type === "delivery" && order.address && (
        <p className="mt-2 text-xs leading-relaxed text-[#B86B77]">{order.address}</p>
      )}
      {order.utr && (
        <p data-testid={`utr-${order.order_id}`} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-green-700/40 bg-green-700/10 px-3 py-1 text-xs font-semibold text-green-800">
          UPI paid • UTR {order.utr} — verify on SmartHub Vyapar
        </p>
      )}

      <ul className="mt-3 divide-y divide-[#E8B4B8]/25 border-y border-[#E8B4B8]/30 text-sm">
        {order.items.map((it) => (
          <li key={it.id} className="flex justify-between py-2">
            <span className="text-[#2C2C2C]"><span className="font-bold text-[#9B2C2C]">{it.qty}×</span> {it.name}</span>
            <span className="font-medium text-[#2C2C2C]">₹{it.price * it.qty}</span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="font-serif text-lg font-bold text-[#9B2C2C]">
          Total ₹{order.total} <span className="text-xs font-normal text-[#B86B77]">(incl. GST ₹{order.gst})</span>
        </p>
        <div className="flex gap-2">
          {order.status === "pending" && (
            <button
              data-testid={`accept-${order.order_id}`}
              onClick={() => onStatus(order.order_id, "preparing")}
              className="rounded-full bg-[#C9A227] px-5 py-2 text-xs font-bold text-white transition-transform hover:scale-105"
            >
              Accept / Preparing
            </button>
          )}
          {order.status !== "completed" && (
            <button
              data-testid={`complete-${order.order_id}`}
              onClick={() => onStatus(order.order_id, "completed")}
              className="rounded-full bg-green-700 px-5 py-2 text-xs font-bold text-white transition-transform hover:scale-105"
            >
              Mark Completed
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
};

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("bloomora-admin-token") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState({});
  const [overrides, setOverrides] = useState({});
  const [extras, setExtras] = useState({ categories: [], items: [] });
  const [layout, setLayout] = useState({ cat_order: [], item_order: {}, hidden_cats: [] });
  const [editItem, setEditItem] = useState(null);
  const [showDishForm, setShowDishForm] = useState(false);
  const [menuVisible, setMenuVisible] = useState(true);
  const [stockCat, setStockCat] = useState("all");
  const [loading, setLoading] = useState(false);

  const headers = useMemo(() => ({ "X-Admin-Token": token, "Content-Type": "application/json" }), [token]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/orders`, { headers });
      if (res.status === 401) { logout(); return; }
      if (res.ok) setOrders(await res.json());
    } catch { /* keep last data */ }
  }, [headers]);

  const loadStock = useCallback(async () => {
    try {
      const res = await fetch(`${API}/menu-stock`);
      if (res.ok) setStock(await res.json());
    } catch { /* keep last data */ }
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/admin/orders`, { headers });
        if (res.ok) { setAuthed(true); setOrders(await res.json()); }
        else sessionStorage.removeItem("bloomora-admin-token");
      } catch { /* offline */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadStock();
    loadExtras();
    fetch(`${API}/menu-overrides`).then((r) => r.json()).then(setOverrides).catch(() => {});
    fetch(`${API}/catalog-layout`).then((r) => r.json()).then(setLayout).catch(() => {});
    fetch(`${API}/site-settings`).then((r) => r.json()).then((d) => setMenuVisible(d.menu_visible !== false)).catch(() => {});
    const t = setInterval(loadOrders, 5000);
    return () => clearInterval(t);
  }, [authed, loadOrders, loadStock]);

  const loadExtras = async () => {
    try {
      const res = await fetch(`${API}/catalog-extra`);
      if (res.ok) setExtras(await res.json());
    } catch { /* keep last data */ }
  };

  const logout = () => {
    sessionStorage.removeItem("bloomora-admin-token");
    setAuthed(false);
    setToken("");
  };

  const setStatus = async (id, status) => {
    setOrders((os) => os.map((o) => (o.order_id === id ? { ...o, status } : o)));
    await fetch(`${API}/admin/orders/${id}`, { method: "PATCH", headers, body: JSON.stringify({ status }) }).catch(() => {});
  };

  const stockOf = (id) => {
    const s = stock[id];
    if (s && typeof s === "object") return { online: s.online !== false, dinein: s.dinein !== false };
    return { online: s !== false, dinein: s !== false };
  };

  const toggleStock = async (itemId, channel) => {
    const cur = stockOf(itemId);
    const next = { ...cur, [channel]: !cur[channel] };
    setStock((s) => ({ ...s, [itemId]: next }));
    const res = await fetch(`${API}/admin/menu-stock/${itemId}`, { method: "PUT", headers, body: JSON.stringify(next) }).catch(() => null);
    if (res && res.status === 401) logout();
  };

  const patchItem = async (itemId, body) => {
    const res = await fetch(`${API}/admin/menu-item/${itemId}`, { method: "PUT", headers, body: JSON.stringify(body) }).catch(() => null);
    if (res && res.status === 401) { logout(); return; }
    if (res && res.ok) setOverrides((o) => ({ ...o, [itemId]: { ...(o[itemId] || {}), ...body } }));
  };

  const uploadImage = async (itemId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/admin/menu-item/${itemId}/image`, { method: "POST", headers: { "X-Admin-Token": token }, body: fd }).catch(() => null);
    if (res && res.status === 401) { logout(); return; }
    if (res && res.ok) {
      const d = await res.json();
      setOverrides((o) => ({ ...o, [itemId]: { ...(o[itemId] || {}), img: d.img } }));
    }
  };

  const setImageUrl = async (itemId) => {
    const url = window.prompt("Paste image URL for this dish:");
    if (url && url.startsWith("http")) await patchItem(itemId, { img: url });
  };

  const toggleMenuVisibility = async () => {
    const next = !menuVisible;
    setMenuVisible(next);
    const res = await fetch(`${API}/admin/site-settings`, { method: "PUT", headers, body: JSON.stringify({ menu_visible: next }) }).catch(() => null);
    if (res && res.status === 401) logout();
  };

  const saveLayout = async (next) => {
    setLayout(next);
    const res = await fetch(`${API}/admin/catalog-layout`, { method: "PUT", headers, body: JSON.stringify(next) }).catch(() => null);
    if (res && res.status === 401) logout();
  };

  const allCatIds = [
    ...ORDER_CATEGORIES.filter((c) => c.id !== "best").map((c) => c.id),
    ...(extras.categories || []).map((c) => c.slug),
  ];
  const orderedCatIds = [...allCatIds].sort((a, b) => {
    const r = (id) => {
      const i = (layout.cat_order || []).indexOf(id);
      return i === -1 ? 999 : i;
    };
    return r(a) - r(b);
  });

  const catMeta = (id) =>
    ORDER_CATEGORIES.find((c) => c.id === id) ||
    (extras.categories || []).map((c) => ({ id: c.slug, name: c.name })).find((c) => c.id === id) ||
    { id, name: id };

  const moveCat = (slug, dir) => {
    const arr = [...orderedCatIds];
    const i = arr.indexOf(slug);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    saveLayout({ ...layout, cat_order: arr });
  };

  const toggleCatHidden = (slug) => {
    const hidden = layout.hidden_cats || [];
    const next = hidden.includes(slug) ? hidden.filter((c) => c !== slug) : [...hidden, slug];
    saveLayout({ ...layout, hidden_cats: next });
  };

  const moveItem = (itemId, dir) => {
    const ids = filteredItems.map((i) => i.id);
    const i = ids.indexOf(itemId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const order = { ...(layout.item_order || {}) };
    ids.forEach((id, idx) => { order[id] = idx; });
    setOverrides((o) => {
      const n = { ...o };
      ids.forEach((id, idx) => { n[id] = { ...(n[id] || {}), order_index: idx }; });
      return n;
    });
    saveLayout({ ...layout, item_order: order });
  };

  const saveEdit = async (id, body) => {
    const res = await fetch(`${API}/admin/menu-item/${id}`, { method: "PUT", headers, body: JSON.stringify(body) }).catch(() => null);
    if (!res) return "Network error";
    if (res.status === 401) { logout(); return "Session expired"; }
    if (!res.ok) { const d = await res.json().catch(() => ({})); return d.detail || "Save failed"; }
    setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), ...body } }));
    return null;
  };

  if (!authed) return <AdminLogin onUnlock={(t) => { setToken(t); setAuthed(true); }} />;

  const applyOv = (i) => {
    const o = overrides[i.id] || {};
    return {
      ...i,
      name: o.name ?? i.name,
      desc: o.desc ?? i.desc,
      veg: o.veg ?? i.veg ?? true,
      best: o.best ?? i.best ?? false,
      sizes: o.sizes ?? i.sizes,
      addons: o.addons ?? i.addons,
      price: o.price ?? i.price,
      img: o.img ?? (i.img || "/images/logo.webp"),
      deleted: o.deleted === true,
      order_index: o.order_index ?? 999,
    };
  };

  const mergedItems = [
    ...ORDER_ITEMS.map(applyOv),
    ...(extras.items || []).map((i) => applyOv({ veg: true, best: false, img: "", ...i })),
  ].sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999));
  const filteredItems = stockCat === "all" ? mergedItems : mergedItems.filter((i) => i.cat === stockCat);

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-6xl px-4 py-10 md:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-black text-[#9B2C2C] md:text-4xl">Bloomora Admin</h1>
          <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[#B86B77]">Orders & Kitchen Stock</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-refresh"
            onClick={() => { setLoading(true); loadOrders().finally(() => setLoading(false)); }}
            className="flex items-center gap-2 rounded-full border-2 border-[#C9A227] px-4 py-2 text-xs font-semibold text-[#9B2C2C] transition-colors hover:bg-[#C9A227] hover:text-white"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="flex items-center gap-2 rounded-full bg-[#9B2C2C] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#7A1D28]"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>

      <div data-testid="admin-tabs" className="mt-8 inline-flex rounded-full border border-[#E8B4B8]/50 bg-white/70 p-1.5 backdrop-blur-sm">
        {[["orders", `Orders (${orders.filter((o) => o.status !== "completed").length} active)`], ["stock", "Menu & Stock"]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${tab === id ? "text-white" : "text-[#9B2C2C]"}`}
          >
            {tab === id && <motion.span layoutId="admin-tab-pill" className="absolute inset-0 rounded-full bg-[#9B2C2C]" transition={{ type: "spring", stiffness: 320, damping: 30 }} />}
            <span className="relative z-10">{label}</span>
          </button>
        ))}
      </div>

      <div data-testid="menu-visibility-banner" className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-[14px] border border-[#C9A227]/40 bg-white/85 p-5 shadow-[0_8px_26px_rgba(155,44,44,0.08)] backdrop-blur-sm">
        <div>
          <p className="font-serif text-lg font-bold text-[#9B2C2C]">Digital Menu Page Visibility</p>
          <p className="max-w-md text-xs leading-relaxed text-[#B86B77]">
            Controls the /menu PDF page and every Menu link across the site. Online ordering always stays live.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="menu-visibility-state" className={`text-xs font-bold uppercase tracking-wider ${menuVisible ? "text-green-700" : "text-[#9B2C2C]"}`}>
            {menuVisible ? "Active" : "Hidden"}
          </span>
          <button
            data-testid="menu-visibility-toggle"
            onClick={toggleMenuVisibility}
            aria-label="Toggle digital menu page visibility"
            className={`relative h-8 w-14 rounded-full transition-colors duration-300 ${menuVisible ? "bg-green-700" : "bg-[#E8B4B8]"}`}
          >
            <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-[left] duration-300 ${menuVisible ? "left-7" : "left-1"}`} />
          </button>
        </div>
      </div>

      {tab === "orders" && (
        <section data-testid="admin-orders-list" className="mt-6 flex flex-col gap-4">
          {orders.length === 0 && (
            <div className="rounded-[14px] border border-dashed border-[#E8B4B8] bg-white/60 p-12 text-center">
              <Blossom className="mx-auto h-10 w-10 text-[#E8B4B8]" />
              <p className="mt-3 font-serif text-lg italic text-[#B86B77]">No orders yet — new orders from /order appear here automatically.</p>
            </div>
          )}
          {orders.map((o) => <OrderCard key={o.order_id} order={o} onStatus={setStatus} />)}
        </section>
      )}

      {tab === "stock" && (
        <section data-testid="admin-stock-list" className="mt-6">
          <div className="flex flex-wrap items-center gap-2 pb-3">
            <button
              data-testid="add-category-button"
              onClick={async () => {
                const name = window.prompt("New category name (e.g. Mocktails):");
                if (!name || name.trim().length < 2) return;
                const res = await fetch(`${API}/admin/categories`, { method: "POST", headers, body: JSON.stringify({ name: name.trim() }) }).catch(() => null);
                if (res && res.status === 401) { logout(); return; }
                if (res && !res.ok) { const d = await res.json().catch(() => ({})); window.alert(d.detail || "Could not add category"); return; }
                loadExtras();
              }}
              className="flex items-center gap-1.5 rounded-full bg-[#C9A227] px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105"
            >
              <Plus size={14} /> Add New Category
            </button>
            <button
              data-testid="add-dish-button"
              onClick={() => setShowDishForm(true)}
              className="flex items-center gap-1.5 rounded-full bg-[#9B2C2C] px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105"
            >
              <Plus size={14} /> Add New Dish
            </button>
          </div>
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-3">
            {[{ id: "all", name: "All" }, ...orderedCatIds.map(catMeta)].map((c) => {
              const hidden = (layout.hidden_cats || []).includes(c.id);
              return (
                <span
                  key={c.id}
                  className={`flex items-center gap-0.5 whitespace-nowrap rounded-full border px-2 py-1 text-xs font-semibold transition-colors ${
                    stockCat === c.id
                      ? "border-[#9B2C2C] bg-[#9B2C2C] text-white"
                      : hidden
                        ? "border-[#E8B4B8]/60 bg-white/50 text-[#B86B77]"
                        : "border-[#E8B4B8]/60 bg-white/70 text-[#9B2C2C] hover:border-[#9B2C2C]"
                  }`}
                >
                  <button data-testid={`stock-cat-${c.id}`} onClick={() => setStockCat(c.id)} className={`px-1.5 ${hidden ? "line-through" : ""}`}>
                    {c.name}
                  </button>
                  {c.id !== "all" && (
                    <>
                      <button data-testid={`cat-left-${c.id}`} onClick={() => moveCat(c.id, -1)} aria-label={`Move ${c.name} earlier`} className="rounded p-0.5 opacity-60 hover:opacity-100"><ChevronLeft size={12} /></button>
                      <button data-testid={`cat-right-${c.id}`} onClick={() => moveCat(c.id, 1)} aria-label={`Move ${c.name} later`} className="rounded p-0.5 opacity-60 hover:opacity-100"><ChevronRight size={12} /></button>
                      <button data-testid={`cat-hide-${c.id}`} onClick={() => toggleCatHidden(c.id)} aria-label={`Toggle ${c.name} visibility`} className="rounded p-0.5 opacity-60 hover:opacity-100">
                        {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    </>
                  )}
                </span>
              );
            })}
          </div>
          <div className="mt-4 overflow-hidden rounded-[14px] border border-[#E8B4B8]/40 bg-white/85 backdrop-blur-sm">
            {filteredItems.map((item) => {
              const so = stockOf(item.id);
              return (
                <div
                  key={item.id}
                  data-testid={`stock-item-${item.id}`}
                  className={`flex flex-wrap items-center justify-between gap-3 border-b border-[#E8B4B8]/25 px-4 py-3.5 last:border-0 ${item.deleted ? "opacity-60" : ""}`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <img
                      src={item.img}
                      alt={item.name}
                      loading="lazy"
                      className={`h-12 w-12 shrink-0 rounded-lg object-cover ${item.deleted ? "grayscale" : ""}`}
                    />
                    <div className="min-w-0">
                      <p className={`truncate text-sm font-semibold ${!item.deleted ? "text-[#2C2C2C]" : "text-[#B86B77] line-through"}`}>
                        {item.name} {item.deleted && <span className="text-[10px] font-bold uppercase">(deleted)</span>}
                        {item.best && <Star size={11} className="mb-0.5 ml-1 inline text-[#C9A227]" fill="currentColor" />}
                      </p>
                      <div className="mt-1">
                        <PriceEditor item={item} onSave={(id, price) => patchItem(id, { price })} />
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                    {stockCat !== "all" && (
                      <span className="flex flex-col">
                        <button data-testid={`move-up-${item.id}`} onClick={() => moveItem(item.id, -1)} className="rounded p-0.5 text-[#9B2C2C] hover:bg-[#E8B4B8]/30" aria-label={`Move ${item.name} up`}><ChevronUp size={13} /></button>
                        <button data-testid={`move-down-${item.id}`} onClick={() => moveItem(item.id, 1)} className="rounded p-0.5 text-[#9B2C2C] hover:bg-[#E8B4B8]/30" aria-label={`Move ${item.name} down`}><ChevronDown size={13} /></button>
                      </span>
                    )}
                    <button
                      data-testid={`best-toggle-${item.id}`}
                      onClick={() => patchItem(item.id, { best: !(item.best === true) })}
                      title="Toggle Bestseller"
                      aria-label={`Toggle bestseller for ${item.name}`}
                      className={`rounded-full border p-2 transition-colors ${item.best ? "border-[#C9A227] bg-[#C9A227]/15 text-[#C9A227]" : "border-[#E8B4B8]/60 text-[#B86B77] hover:border-[#C9A227]"}`}
                    >
                      <Star size={15} fill={item.best ? "currentColor" : "none"} />
                    </button>
                    <button
                      data-testid={`edit-item-${item.id}`}
                      onClick={() => setEditItem(item)}
                      title="Edit dish (name, price, description, sizes)"
                      aria-label={`Edit ${item.name}`}
                      className="rounded-full border border-[#E8B4B8]/60 p-2 text-[#9B2C2C] transition-colors hover:bg-[#E8B4B8]/30"
                    >
                      <Pencil size={15} />
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`file-${item.id}`}
                      onChange={(e) => { uploadImage(item.id, e.target.files?.[0]); e.target.value = ""; }}
                    />
                    <label
                      htmlFor={`file-${item.id}`}
                      data-testid={`upload-image-${item.id}`}
                      title="Upload / Replace image"
                      className="cursor-pointer rounded-full border border-[#E8B4B8]/60 p-2 text-[#9B2C2C] transition-colors hover:bg-[#E8B4B8]/30"
                    >
                      <ImagePlus size={15} />
                    </label>
                    <button
                      data-testid={`image-url-${item.id}`}
                      onClick={() => setImageUrl(item.id)}
                      title="Set image from URL"
                      aria-label={`Set image URL for ${item.name}`}
                      className="rounded-full border border-[#E8B4B8]/60 p-2 text-[#9B2C2C] transition-colors hover:bg-[#E8B4B8]/30"
                    >
                      <Link2 size={15} />
                    </button>
                    <button
                      data-testid={`delete-item-${item.id}`}
                      onClick={() => patchItem(item.id, { deleted: !item.deleted })}
                      title={item.deleted ? "Restore item" : "Delete item"}
                      aria-label={item.deleted ? `Restore ${item.name}` : `Delete ${item.name}`}
                      className={`rounded-full border p-2 transition-colors ${
                        item.deleted ? "border-green-700/50 text-green-700 hover:bg-green-700/10" : "border-[#E8B4B8]/60 text-[#9B2C2C] hover:bg-[#9B2C2C]/10"
                      }`}
                    >
                      {item.deleted ? <Undo2 size={15} /> : <Trash2 size={15} />}
                    </button>
                    <span className="mx-1 hidden h-6 w-px bg-[#E8B4B8]/60 sm:block" aria-hidden="true" />
                    <MiniToggle on={so.online} onClick={() => toggleStock(item.id, "online")} testid={`stock-online-${item.id}`} label="Online" />
                    <MiniToggle on={so.dinein} onClick={() => toggleStock(item.id, "dinein")} testid={`stock-dinein-${item.id}`} label="Dine-in" />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <p className="mt-10 text-center text-xs text-[#B86B77]">
        <Link to="/" className="underline-offset-4 hover:underline">← Back to Bloomora website</Link>
      </p>

      {showDishForm && (
        <AddDishModal
          categories={[...ORDER_CATEGORIES.filter((c) => c.id !== "best"), ...(extras.categories || []).map((c) => ({ id: c.slug, name: c.name }))]}
          defaultCat={stockCat}
          onClose={() => setShowDishForm(false)}
          onSaved={loadExtras}
          headers={headers}
          onUnauthorized={logout}
        />
      )}

      {editItem && (
        <EditDishModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSave={saveEdit}
        />
      )}
    </main>
  );
}
